/**
 * Flight history — the telemetry workbench.
 *
 * A full-bleed map of the AOR carries the selected sortie's ground track; three
 * floating glass panels sit over it. Left is the flight list, right is the
 * selected flight's summary and its command ledger, and the strip along the
 * bottom is the workbench: the recorded channels drawn on one shared time axis
 * with a scrubber.
 *
 * Replay drives the live telemetry path on the server, so a replayed aircraft
 * appears in the fleet and on the map exactly like a flying one. That is the
 * right design and also the one thing here that can mislead somebody badly, so
 * the amber banner is not decoration: while a replay is loaded, the screen says
 * so in words, with the callsign of the aircraft being impersonated.
 *
 * Everything is scoped to the AOR the operator is in — a sortie whose launch
 * point sits in another AOR is not in this list.
 */

import { TriangleAlert } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { summarizeFlight } from '@/api/ai';
import {
  deleteFlight,
  getFlightEvents,
  getFlightStatistics,
  getFlightTelemetry,
  getReplayStatus,
  listFlights,
  loadReplay,
  MAX_NOTES_CHARS,
  pauseReplay,
  playReplay,
  seekReplay,
  setFlightNotes,
  setReplaySpeed,
  stopReplay,
  unloadReplay,
  type FlightEvent,
  type FlightNotes,
  type FlightSession,
  type FlightStatistics,
  type ReplayStatus,
  type TelemetryPoint,
} from '@/api/flights';
import { formatBytes, getStorage, type StorageReport } from '@/api/storage';
import { aorById, positionInAor } from '@/map/aors';
import {
  geoJsonSource,
  MapCanvas,
  useMapController,
  type MapLike,
} from '@/map/MapCanvas';
import { useDroneMarkers } from '@/map/useDroneMarkers';
import { useViewport } from '@/mobile/useViewport';
import { teamColorFor } from '@/features/fleet/FleetRail';
import { useCanCommand, useHasRole } from '@/stores/authStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.55)';

/** How often the replay position is re-read while something is playing. */
const REPLAY_POLL_MS = 500;
/** Speeds offered on the workbench transport. The server caps at 8x. */
const WORKBENCH_SPEEDS = [1, 2, 4, 8] as const;

function mmss(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '--:--';
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function hms(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function stamp(unixSeconds: number): string {
  return new Date(unixSeconds * 1000)
    .toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .toUpperCase();
}

function metres(value: number | null): string {
  if (value === null) return '---';
  return value < 1000 ? `${value.toFixed(0)} m` : `${(value / 1000).toFixed(2)} km`;
}

/**
 * The extended rollups are null when the aircraft never reported the reading,
 * which is different from a reading of zero, so each shows "---" rather than
 * 0 when absent. `== null` also covers a server that predates the columns.
 */
export function energyText(session: FlightSession): string {
  if (session.whUsed == null) {
    return session.mahUsed == null ? '---' : `${session.mahUsed.toFixed(0)} mAh`;
  }
  return `${session.whUsed.toFixed(1)} Wh`;
}

export function windText(session: FlightSession): string {
  if (session.avgWindMs == null || session.maxWindMs == null) return '---';
  return `${session.avgWindMs.toFixed(1)} / ${session.maxWindMs.toFixed(1)} m/s`;
}

export function vibeText(session: FlightSession): string {
  if (session.maxVibe == null) return '---';
  const clips = session.clippingEvents ? ` · ${session.clippingEvents} clip` : '';
  return `${session.maxVibe.toFixed(1)} m/s²${clips}`;
}

const END_REASON_TEXT: Record<string, string> = {
  disarmed: 'Disarmed',
  user_disconnect: 'Disconnected',
  signal_loss: 'Signal lost',
  link_lost: 'Link lost',
  link_closed: 'Link closed',
  server_stop: 'Server stopped',
};

/** Why the recording ended. A server stop is called out because those
 *  flights were closed at the last telemetry received, not at a landing. */
export function endedText(session: FlightSession): string {
  if (session.inProgress) return 'Recording';
  if (!session.endReason) return '---';
  return END_REASON_TEXT[session.endReason] ?? 'Unknown';
}

/** Under a tenth of the volume free is when somebody should start planning an export. */
export function storageLow(storage: StorageReport): boolean {
  const { diskFreeBytes: free, diskTotalBytes: total } = storage;
  return free !== null && total !== null && total > 0 && free / total < 0.1;
}

/**
 * A bench test is an arm that never became a flight: no altitude, no distance.
 * Same thresholds the server's is_real_flight uses to discard auto-recorded
 * non-flights — sessions kept despite failing them were started by hand.
 *
 * A boat or rover never climbs, so it is judged on how far it got from its
 * first fix, as the server judges it. Testing it on altitude filed every boat
 * run under BENCH and hid it behind the default filter.
 */
export function isBench(session: FlightSession): boolean {
  if (session.vehicleClass === 'boat' || session.vehicleClass === 'rover') {
    return (session.maxDistanceM ?? 0) < 20;
  }
  return (session.maxAltitudeM ?? 0) < 5 && (session.totalDistanceM ?? 0) < 50;
}

/** COMPLETED / LINK LOST / LIVE — what the outcome pill says, and its colour. */
function outcomeOf(session: FlightSession): { label: string; color: string } {
  if (session.inProgress) return { label: 'LIVE', color: WARN };
  if (session.errors > 0 || session.endReason === 'link_lost')
    return { label: 'LINK LOST', color: DANGER };
  if (session.geofenceBreaches > 0) return { label: 'BREACH', color: DANGER };
  if (isBench(session)) return { label: 'BENCH TEST', color: MUTED };
  return { label: 'COMPLETED', color: OK };
}

// ── Telemetry channels ──────────────────────────────────────────────────────

interface Channel {
  key: string;
  color: string;
  accessor: (point: TelemetryPoint) => number | null;
  format: (value: number) => string;
}

const CHANNELS: Channel[] = [
  {
    key: 'ALT',
    color: 'rgba(255,255,255,0.85)',
    accessor: (p) => p.relativeAltM,
    format: (v) => `${v.toFixed(0)} m`,
  },
  {
    key: 'SPD',
    color: 'rgba(255,255,255,0.55)',
    accessor: (p) => p.groundSpeedMs,
    format: (v) => `${v.toFixed(1)} m/s`,
  },
  {
    key: 'BAT',
    color: OK,
    accessor: (p) => p.batteryPercent,
    format: (v) => `${v.toFixed(0)}%`,
  },
  {
    key: 'SATS',
    color: 'rgba(255,255,255,0.4)',
    accessor: (p) => p.satellites,
    format: (v) => `${v.toFixed(0)} sv`,
  },
];

/** At most this many samples per lane — enough to read the shape, cheap to draw. */
const LANE_SAMPLES = 320;

interface Lane {
  key: string;
  color: string;
  points: string;
  value: string;
  valueColor: string;
  empty: boolean;
}

/**
 * Downsample telemetry to a fixed width and build one normalised polyline per
 * channel, plus the value sitting under the scrubber. Each channel is scaled to
 * its own min/max because metres, m/s and percent share no useful range.
 */
function buildLanes(points: TelemetryPoint[], t: number): Lane[] {
  const n = points.length;
  if (n < 2) {
    return CHANNELS.map((c) => ({
      key: c.key,
      color: c.color,
      points: '',
      value: '---',
      valueColor: MUTED,
      empty: true,
    }));
  }

  const step = Math.max(1, Math.floor(n / LANE_SAMPLES));
  const sampled: TelemetryPoint[] = [];
  for (let i = 0; i < n; i += step) sampled.push(points[i]!);
  if (sampled[sampled.length - 1] !== points[n - 1]) sampled.push(points[n - 1]!);
  const width = sampled.length - 1;

  const cursor = Math.min(sampled.length - 1, Math.round(t * width));

  return CHANNELS.map((channel) => {
    const values = sampled.map(channel.accessor);
    const real = values.filter((v): v is number => v !== null && Number.isFinite(v));
    if (real.length < 2) {
      return {
        key: channel.key,
        color: channel.color,
        points: '',
        value: '---',
        valueColor: MUTED,
        empty: true,
      };
    }

    const min = Math.min(...real);
    const max = Math.max(...real);
    const span = max - min || 1;

    let last = real[0]!;
    const coords = values.map((v, i) => {
      if (v !== null && Number.isFinite(v)) last = v;
      const norm = (last - min) / span;
      const x = ((i / width) * 600).toFixed(1);
      const y = (28 - norm * 26).toFixed(1);
      return `${x},${y}`;
    });

    const here = values[cursor];
    const shown = here != null && Number.isFinite(here) ? here : last;
    const low = channel.key === 'BAT' && shown < 30;

    return {
      key: channel.key,
      color: channel.color,
      points: coords.join(' '),
      value: channel.format(shown),
      valueColor: low ? WARN : channel.key === 'ALT' ? '#f4f4f5' : MUTED,
      empty: false,
    };
  });
}

// ── Map track ────────────────────────────────────────────────────────────────

const TRACK_SOURCE = 'flight-track';
const TRACK_LINE = 'flight-track-line';
const TRACK_ENDS = 'flight-track-ends';
const TRACK_NOW = 'flight-track-now';

/** Which sample the playhead is sitting on, from the replay's own clock. */
export function cursorFor(status: ReplayStatus | null, count: number): number {
  if (!status || !count || status.duration <= 0) return 0;
  const fraction = Math.min(1, Math.max(0, status.offset / status.duration));
  return Math.round(fraction * (count - 1));
}

/** Where the aircraft was at the playhead, as a feature the map can draw.
 *
 * The replay drew the ground track and the two ends and nothing else, so
 * scrubbing moved the charts while the map sat perfectly still — an operator
 * reviewing a sortie could see the shape of the flight but never where the
 * aircraft actually was at the moment they were looking at. */
export function playheadFeature(points: TelemetryPoint[], cursor: number): unknown[] {
  const fixes = points.filter(
    (p) => p.lat !== null && p.lon !== null && !(p.lat === 0 && p.lon === 0),
  );
  if (!fixes.length) return [];
  const at = fixes[Math.min(fixes.length - 1, Math.max(0, cursor))];
  if (!at) return [];
  return [
    {
      type: 'Feature',
      properties: { kind: 'now' },
      geometry: { type: 'Point', coordinates: [at.lon as number, at.lat as number] },
    },
  ];
}

function trackFeatures(points: TelemetryPoint[]): unknown[] {
  const line = points
    .filter((p) => p.lat !== null && p.lon !== null && !(p.lat === 0 && p.lon === 0))
    .map((p) => [p.lon as number, p.lat as number]);
  if (line.length < 2) return [];
  const features: unknown[] = [
    {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: line },
    },
    {
      type: 'Feature',
      properties: { kind: 'start' },
      geometry: { type: 'Point', coordinates: line[0] },
    },
    {
      type: 'Feature',
      properties: { kind: 'end' },
      geometry: { type: 'Point', coordinates: line[line.length - 1] },
    },
  ];
  return features;
}

/** Draw the selected sortie's ground track on the map and frame it. */
function useFlightTrack(points: TelemetryPoint[], cursor = 0): void {
  const controller = useMapController();

  useEffect(() => {
    return controller.ready((map: MapLike) => {
      if (!map.getSource(TRACK_SOURCE)) {
        map.addSource(TRACK_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      }
      if (!map.getLayer(TRACK_LINE)) {
        map.addLayer({
          id: TRACK_LINE,
          type: 'line',
          source: TRACK_SOURCE,
          filter: ['==', ['geometry-type'], 'LineString'],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#f4f4f5', 'line-width': 2.4, 'line-opacity': 0.9 },
        });
      }
      if (!map.getLayer(TRACK_NOW)) {
        // Added after the ends so the moving aircraft is never hidden under the
        // start or finish dot, which is exactly where a replay begins.
        map.addLayer({
          id: TRACK_NOW,
          type: 'circle',
          source: TRACK_SOURCE,
          filter: ['==', ['get', 'kind'], 'now'],
          paint: {
            'circle-radius': 7,
            'circle-color': '#4ea1ff',
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
          },
        });
      }
      if (!map.getLayer(TRACK_ENDS)) {
        map.addLayer({
          id: TRACK_ENDS,
          type: 'circle',
          source: TRACK_SOURCE,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': 5,
            'circle-color': ['match', ['get', 'kind'], 'start', OK, DANGER],
            'circle-stroke-color': 'rgba(0,0,0,0.7)',
            'circle-stroke-width': 1.5,
          },
        });
      }
      return () => {
        for (const id of [TRACK_NOW, TRACK_ENDS, TRACK_LINE])
          if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(TRACK_SOURCE)) map.removeSource(TRACK_SOURCE);
      };
    });
  }, [controller]);

  useEffect(() => {
    const write = (map: MapLike): void => {
      const source = geoJsonSource(map, TRACK_SOURCE);
      if (!source) return;
      source.setData({
        type: 'FeatureCollection',
        features: [...trackFeatures(points), ...playheadFeature(points, cursor)],
      });
      const line = points
        .filter((p) => p.lat !== null && p.lon !== null && !(p.lat === 0 && p.lon === 0))
        .map((p) => [p.lon as number, p.lat as number] as [number, number]);
      if (line.length >= 2) controller.fitPoints(line, 140);
    };
    // The map may still be loading when the first flight's telemetry arrives.
    // Writing now if it is ready, and otherwise on load, keeps the very first
    // selected track from silently never drawing.
    const map = controller.instance();
    if (map) {
      write(map);
      return;
    }
    return controller.ready((ready) => write(ready));
  }, [controller, points, cursor]);
}

// ── Filter pill ──────────────────────────────────────────────────────────────

function FilterPill({
  label,
  on,
  onClick,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border px-2 py-[3px] font-mono text-[8.5px] font-semibold"
      style={{
        background: on ? 'rgba(255,255,255,0.9)' : 'transparent',
        color: on ? '#0c0c0d' : MUTED,
        borderColor: on ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.14)',
      }}
    >
      {label}
    </button>
  );
}

// ── Left panel: flight list ──────────────────────────────────────────────────

function FlightList({
  sessions,
  loading,
  selectedId,
  replaySessionId,
  statistics,
  storage,
  onSelect,
  onRefresh,
  filters,
  onToggle,
}: {
  sessions: FlightSession[];
  loading: boolean;
  selectedId: string | null;
  replaySessionId: string | null;
  statistics: FlightStatistics | null;
  storage: StorageReport | null;
  onSelect: (id: string) => void;
  onRefresh: () => void;
  filters: { real: boolean; bench: boolean };
  onToggle: (which: 'real' | 'bench') => void;
}) {
  return (
    <aside className="flt-list glass scroll-thin pointer-events-auto absolute left-[14px] top-[var(--flt-top)] bottom-[250px] flex w-[var(--flt-list-w)] flex-col overflow-y-auto rounded-[20px] tablet:top-[calc(var(--flt-top)+46px)] tablet:z-10">
      <div className="flex flex-none items-center gap-2 px-4 pt-3 pb-2">
        <span className="label-mono text-[10px]">FLIGHTS</span>
        <span className="font-mono text-[10.5px] text-muted-foreground">{sessions.length}</span>
        <div className="ml-auto flex gap-1">
          <FilterPill label="REAL" on={filters.real} onClick={() => onToggle('real')} />
          <FilterPill label="BENCH" on={filters.bench} onClick={() => onToggle('bench')} />
          <button
            type="button"
            onClick={onRefresh}
            className="rounded-full border border-white/15 px-2 py-[3px] font-mono text-[8.5px] font-semibold text-muted-foreground"
          >
            ↻
          </button>
        </div>
      </div>

      {statistics ? (
        <div className="mx-2.5 mb-1.5 grid flex-none grid-cols-2 gap-x-3 gap-y-0.5 rounded-[12px] bg-black/20 px-3 py-2 font-mono text-[9.5px] text-white/50">
          <span>{statistics.totalFlights} flights all-time</span>
          <span>{statistics.totalFlightTimeHours.toFixed(1)} h airborne</span>
          <span>{statistics.totalDistanceKm.toFixed(1)} km flown</span>
          <span>{statistics.highestAltitudeM.toFixed(0)} m highest</span>
        </div>
      ) : null}

      {storage ? (
        <div
          className="mx-2.5 mb-1.5 flex flex-none flex-wrap gap-x-3 rounded-[12px] bg-black/20 px-3 py-1.5 font-mono text-[9.5px] text-white/50"
          title="Flight logs are kept forever. Only standby telemetry from a disarmed aircraft rolls."
        >
          <span>{formatBytes(storage.totalBytes)} of logs</span>
          <span style={storageLow(storage) ? { color: WARN } : undefined}>
            {formatBytes(storage.diskFreeBytes)} disk free
          </span>
        </div>
      ) : null}

      <div className="flex flex-1 flex-col gap-1 px-2.5 pb-2.5">
        {loading ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">Loading flight history.</p>
        ) : sessions.length === 0 ? (
          <p className="px-2 py-3 text-xs text-muted-foreground">
            No sorties recorded in this AOR yet. A session starts when an aircraft arms and is kept
            once it has been airborne for half a minute.
          </p>
        ) : (
          sessions.map((session) => {
            const outcome = outcomeOf(session);
            const selected = session.sessionId === selectedId;
            return (
              <button
                type="button"
                key={session.sessionId}
                onClick={() => onSelect(session.sessionId)}
                className="flex flex-col gap-1 rounded-[13px] border px-3 py-2.5 text-left"
                style={{
                  borderColor: selected ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.05)',
                  background: selected ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.025)',
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[12px] font-semibold">{session.droneId}</span>
                  {session.teamCallsign ? (
                    <span
                      className="rounded-full bg-white/8 px-[6px] py-[2px] font-mono text-[8px] font-semibold text-white/60"
                      title={`Flown by ${session.teamCallsign}`}
                    >
                      {session.teamCallsign}
                    </span>
                  ) : null}
                  <span
                    className="rounded-full border border-white/10 bg-white/5 px-[7px] py-[2px] font-mono text-[8.5px] font-semibold"
                    style={{ color: outcome.color }}
                  >
                    {outcome.label}
                  </span>
                  {session.sessionId === replaySessionId ? (
                    <span
                      className="rounded-full px-[6px] py-[2px] font-mono text-[7.5px] font-bold"
                      style={{ background: WARN, color: '#1c1503' }}
                    >
                      REPLAYING
                    </span>
                  ) : null}
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                    {stamp(session.startTime)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2.5 font-mono text-[10px] text-muted-foreground">
                  <span>{mmss(session.flightTimeS)}</span>
                  <span>▲ {session.maxAltitudeM === null ? '---' : `${session.maxAltitudeM.toFixed(0)} m`}</span>
                  <span>➤ {session.maxSpeedMs === null ? '---' : `${session.maxSpeedMs.toFixed(1)} m/s`}</span>
                  <span>{metres(session.totalDistanceM)}</span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}

// ── Right panel: summary + ledger + export ───────────────────────────────────

function ledgerColor(severity: string): string {
  if (severity === 'error') return DANGER;
  if (severity === 'warning') return WARN;
  return OK;
}

function ledgerResult(severity: string): string {
  if (severity === 'error') return 'ERROR';
  if (severity === 'warning') return 'CAUTION';
  return 'OK';
}

function positioned(points: TelemetryPoint[]): TelemetryPoint[] {
  return points.filter((p) => p.lat !== null && p.lon !== null);
}

/** A KML track for Google Earth, where a lot of post-flight review happens and
 *  where GeoJSON will not open. Returns '' when there is nothing to draw, so the
 *  caller can say so rather than export an empty document. Coordinates are
 *  lon,lat,alt per the KML spec — the order everyone gets wrong. */
export function buildKml(droneId: string, sessionId: string, points: TelemetryPoint[]): string {
  const fixes = positioned(points);
  if (!fixes.length) return '';
  const coords = fixes.map((p) => `${p.lon},${p.lat},${p.relativeAltM ?? 0}`).join(' ');
  const placemark = (name: string, p: TelemetryPoint): string =>
    `<Placemark><name>${name}</name><Point><coordinates>${p.lon},${p.lat},${p.relativeAltM ?? 0}</coordinates></Point></Placemark>`;
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<kml xmlns="http://www.opengis.net/kml/2.2"><Document>' +
    `<name>${droneId} ${sessionId}</name>` +
    '<Placemark><name>Track</name><Style><LineStyle><color>ff4ea1ff</color><width>3</width></LineStyle></Style>' +
    `<LineString><altitudeMode>relativeToGround</altitudeMode><coordinates>${coords}</coordinates></LineString></Placemark>` +
    placemark('Start', fixes[0]!) +
    placemark('End', fixes[fixes.length - 1]!) +
    '</Document></kml>'
  );
}

/** A GPX track — the interchange format for other mapping tools and GPS units.
 *  '' when there are no positioned fixes. lat/lon attributes, ISO time. */
export function buildGpx(droneId: string, sessionId: string, points: TelemetryPoint[]): string {
  const fixes = positioned(points);
  if (!fixes.length) return '';
  const pts = fixes
    .map(
      (p) =>
        `<trkpt lat="${p.lat}" lon="${p.lon}"><ele>${p.relativeAltM ?? 0}</ele>` +
        `<time>${new Date(p.timestamp).toISOString()}</time></trkpt>`,
    )
    .join('');
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<gpx version="1.1" creator="Parallax" xmlns="http://www.topografix.com/GPX/1/1">' +
    `<trk><name>${droneId} ${sessionId}</name><trkseg>${pts}</trkseg></trk></gpx>`
  );
}

function download(name: string, mime: string, body: string): void {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The debrief. Written after the aircraft is down, which is why it has to be
 * editable on a finished flight: before this the column could only be filled
 * by the code that ended the session, and nothing ever filled it.
 *
 * Keyed on the session by the caller, so switching flights starts a fresh
 * draft rather than carrying one flight's unsaved text onto the next.
 */
export function NotesEditor({
  session,
  onSaved,
}: {
  session: FlightSession;
  onSaved: (notes: FlightNotes) => void;
}) {
  const canWrite = useCanCommand();
  const [draft, setDraft] = useState(session.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = draft.trim() !== (session.notes ?? '').trim();

  const save = () => {
    setSaving(true);
    setError(null);
    void setFlightNotes(session.sessionId, draft)
      .then((result) => {
        if (result.ok) onSaved(result.data);
        else setError(result.error.message);
      })
      .finally(() => setSaving(false));
  };

  const edited =
    session.notesBy && session.notesAt
      ? `Last edited by ${session.notesBy}, ${stamp(session.notesAt)}`
      : null;

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <label htmlFor={`notes-${session.sessionId}`} className="label-mono text-[9px]">
          DEBRIEF NOTES
        </label>
        {canWrite ? (
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="ml-auto rounded-full bg-white/90 px-3 py-[5px] font-mono text-[8.5px] font-bold text-[#0c0c0d] disabled:opacity-40"
          >
            {saving ? 'SAVING…' : 'SAVE NOTES'}
          </button>
        ) : null}
      </div>
      {canWrite ? (
        <textarea
          id={`notes-${session.sessionId}`}
          value={draft}
          maxLength={MAX_NOTES_CHARS}
          onChange={(event) => setDraft(event.target.value)}
          rows={4}
          placeholder="What happened, what to check before the next sortie."
          className="w-full resize-y rounded-[12px] border border-white/10 bg-black/25 px-3 py-2 text-[11px] leading-relaxed text-white/90 outline-none focus:border-white/30"
        />
      ) : (
        <p
          id={`notes-${session.sessionId}`}
          className="whitespace-pre-wrap rounded-[12px] bg-black/20 px-3 py-2 text-[11px] leading-relaxed text-white/80"
        >
          {session.notes ?? 'No debrief written for this flight.'}
        </p>
      )}
      {edited ? <div className="mt-1 text-[9.5px] text-white/40">{edited}</div> : null}
      {error ? (
        <div className="mt-1 font-mono text-[10px]" style={{ color: WARN }}>
          Notes not saved: {error}
        </div>
      ) : null}
    </div>
  );
}

function FlightSummary({
  session,
  points,
  events,
  onDeleted,
  onNotesSaved,
  status,
  onStatus,
}: {
  session: FlightSession;
  points: TelemetryPoint[];
  events: FlightEvent[];
  onDeleted: () => void;
  onNotesSaved: (notes: FlightNotes) => void;
  status: ReplayStatus | null;
  onStatus: (status: ReplayStatus) => void;
}) {
  const canCommand = useCanCommand();
  const [exMsg, setExMsg] = useState<{ text: string; warn: boolean } | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);

  const runAiSummary = () => {
    setAiBusy(true);
    setAiSummary(null);
    void summarizeFlight(session.sessionId)
      .then((result) => {
        setAiSummary(result.ok ? result.data.summary : result.error.message);
      })
      .finally(() => setAiBusy(false));
  };
  const [delArmed, setDelArmed] = useState(false);
  const outcome = outcomeOf(session);

  const battery = useMemo(() => {
    const withBat = points.filter((p) => p.batteryPercent !== null);
    const first = withBat[0]?.batteryPercent;
    const last = withBat[withBat.length - 1]?.batteryPercent;
    if (first === undefined || last === undefined || first === null || last === null) return '---';
    return `${first.toFixed(0)}% → ${last.toFixed(0)}%`;
  }, [points]);

  const cautions = events.filter((e) => e.severity === 'warning').length;
  const errors = events.filter((e) => e.severity === 'error').length;

  const stats: Array<{ k: string; v: string }> = [
    { k: 'FLOWN BY', v: session.teamCallsign ?? '---' },
    { k: 'ENDED', v: endedText(session) },
    { k: 'DURATION', v: mmss(session.flightTimeS) },
    { k: 'DISTANCE', v: metres(session.totalDistanceM) },
    { k: 'MAX ALT', v: session.maxAltitudeM === null ? '---' : `${session.maxAltitudeM.toFixed(0)} m AGL` },
    { k: 'MAX SPEED', v: session.maxSpeedMs === null ? '---' : `${session.maxSpeedMs.toFixed(1)} m/s` },
    { k: 'BATTERY USED', v: battery },
    { k: 'EVENTS', v: `${events.length}${cautions ? ` · ${cautions} caution` : ''}${errors ? ` · ${errors} err` : ''}` },
    { k: 'ENERGY USED', v: energyText(session) },
    { k: 'WIND AVG / MAX', v: windText(session) },
    { k: 'PEAK VIBRATION', v: vibeText(session) },
    { k: 'EKF PEAK', v: session.maxEkfVariance == null ? '---' : session.maxEkfVariance.toFixed(2) },
  ];

  const exportCsv = () => {
    const header = 'timestamp,lat,lon,rel_alt_m,ground_speed_ms,battery_pct,satellites';
    const rows = points.map((p) =>
      [p.timestamp, p.lat ?? '', p.lon ?? '', p.relativeAltM ?? '', p.groundSpeedMs ?? '', p.batteryPercent ?? '', p.satellites ?? ''].join(','),
    );
    download(`${session.droneId}_${session.sessionId}.csv`, 'text/csv', [header, ...rows].join('\n'));
    setExMsg({ text: `Exported telemetry CSV (${rows.length} rows)`, warn: false });
  };

  const exportGeo = () => {
    const coords = points
      .filter((p) => p.lat !== null && p.lon !== null)
      .map((p) => [p.lon, p.lat, p.relativeAltM ?? 0]);
    const geojson = {
      type: 'Feature',
      properties: { drone: session.droneId, session: session.sessionId, start: session.startTime },
      geometry: { type: 'LineString', coordinates: coords },
    };
    download(`${session.droneId}_${session.sessionId}.geojson`, 'application/geo+json', JSON.stringify(geojson, null, 2));
    setExMsg({ text: `Exported ground track as GeoJSON (${coords.length} fixes)`, warn: false });
  };

  const exportKml = () => {
    const kml = buildKml(session.droneId, session.sessionId, points);
    if (!kml) {
      setExMsg({ text: 'No positioned fixes to export', warn: true });
      return;
    }
    download(
      `${session.droneId}_${session.sessionId}.kml`,
      'application/vnd.google-earth.kml+xml',
      kml,
    );
    setExMsg({ text: 'Exported track as KML (Google Earth)', warn: false });
  };

  const exportGpx = () => {
    const gpx = buildGpx(session.droneId, session.sessionId, points);
    if (!gpx) {
      setExMsg({ text: 'No positioned fixes to export', warn: true });
      return;
    }
    download(`${session.droneId}_${session.sessionId}.gpx`, 'application/gpx+xml', gpx);
    setExMsg({ text: 'Exported track as GPX', warn: false });
  };

  const exportMcap = () =>
    setExMsg({
      text: 'MCAP packaging runs in the server-side recorder — not wired into this build. Use CSV, KML or GPX here.',
      warn: true,
    });

  const doDelete = () => {
    if (!delArmed) {
      setDelArmed(true);
      setExMsg({ text: 'Deleting a session is permanent — press again to confirm', warn: true });
      return;
    }
    void deleteFlight(session.sessionId).then((result) => {
      setDelArmed(false);
      if (result.ok) onDeleted();
      else setExMsg({ text: 'Delete failed', warn: true });
    });
  };

  return (
    <aside className="flt-detail glass scroll-thin pointer-events-auto absolute right-[14px] top-[var(--flt-top)] bottom-[250px] flex w-[var(--flt-detail-w)] flex-col overflow-y-auto rounded-[20px]">
      <div className="flex flex-col gap-3 p-3.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[13px] font-bold">
            {session.droneId} · {stamp(session.startTime)}
          </span>
          <span
            className="rounded-full border px-2 py-[2px] font-mono text-[8.5px] font-semibold"
            style={{ color: outcome.color, borderColor: `color-mix(in oklch, ${outcome.color} 45%, transparent)` }}
          >
            {outcome.label}
          </span>
        </div>

        <CompactReplay session={session} status={status} onStatus={onStatus} />

        <div className="grid grid-cols-2 gap-1.5">
          {stats.map((stat) => (
            <div key={stat.k} className="rounded-[12px] border border-white/8 bg-white/5 px-3 py-2">
              <div className="label-mono text-[8.5px]">{stat.k}</div>
              <div className="mt-0.5 truncate font-mono text-[12.5px]" title={stat.v}>
                {stat.v}
              </div>
            </div>
          ))}
        </div>

        <NotesEditor key={session.sessionId} session={session} onSaved={onNotesSaved} />

        <div>
          <div className="label-mono mb-2 text-[9px]">COMMAND LEDGER — AUDIT TRAIL</div>
          <div className="flex flex-col gap-1 font-mono text-[10.5px]">
            {events.length === 0 ? (
              <p className="px-2 py-1 text-[10px] text-muted-foreground">
                Nothing was flagged during this flight.
              </p>
            ) : (
              events.slice(0, 40).map((event, index) => (
                <div
                  key={index}
                  className="flex items-baseline gap-2 rounded-[10px] px-2.5 py-1.5"
                  style={{ background: index % 2 ? 'transparent' : 'rgba(255,255,255,0.03)' }}
                >
                  <span className="whitespace-nowrap text-white/40">{hms(event.timestamp)}</span>
                  <span
                    className="min-w-0 flex-1 truncate"
                    title={`${event.eventType}${event.message ? ` — ${event.message}` : ''}`}
                  >
                    {event.eventType}
                    {event.message ? ` — ${event.message}` : ''}
                  </span>
                  <span className="whitespace-nowrap" style={{ color: ledgerColor(event.severity) }}>
                    {ledgerResult(event.severity)}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="mt-1.5 text-[9.5px] text-white/40">
            Operator, parameters and result for every command — compliance-grade, recorded by the
            backend.
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="label-mono text-[9px]">EXPORT</span>
            <button
              type="button"
              disabled={aiBusy}
              onClick={runAiSummary}
              className="ml-auto rounded-full border border-white/15 bg-white/5 px-3 py-[5px] font-mono text-[8.5px] font-bold text-white/70 disabled:opacity-40"
            >
              {aiBusy ? 'SUMMARIZING…' : '⚚ AI SUMMARY'}
            </button>
          </div>
          {aiSummary ? (
            <div className="mb-2 rounded-[12px] border border-white/10 bg-black/25 px-3 py-2.5">
              <p className="whitespace-pre-wrap text-[10.5px] leading-relaxed">{aiSummary}</p>
              <p className="mt-1 label-mono text-[7.5px] text-white/40">
                AI-GENERATED — ADVISORY, VERIFY AGAINST THE LEDGER
              </p>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={exportMcap}
              className="rounded-full bg-white/90 px-3 py-[7px] font-mono text-[9px] font-bold text-[#0c0c0d]"
            >
              MCAP
            </button>
            <button
              type="button"
              onClick={exportCsv}
              className="rounded-full border border-white/15 bg-white/5 px-3 py-[7px] font-mono text-[9px] font-bold text-white/70"
            >
              CSV
            </button>
            <button
              type="button"
              onClick={exportGeo}
              className="rounded-full border border-white/15 bg-white/5 px-3 py-[7px] font-mono text-[9px] font-bold text-white/70"
            >
              GEOJSON TRACK
            </button>
            <button
              type="button"
              onClick={exportKml}
              className="rounded-full border border-white/15 bg-white/5 px-3 py-[7px] font-mono text-[9px] font-bold text-white/70"
            >
              KML
            </button>
            <button
              type="button"
              onClick={exportGpx}
              className="rounded-full border border-white/15 bg-white/5 px-3 py-[7px] font-mono text-[9px] font-bold text-white/70"
            >
              GPX
            </button>
            {canCommand ? (
              <button
                type="button"
                onClick={doDelete}
                className="rounded-full border bg-transparent px-3 py-[7px] font-mono text-[9px] font-bold"
                style={{ color: DANGER, borderColor: `color-mix(in oklch, ${DANGER} 50%, transparent)` }}
              >
                {delArmed ? 'CONFIRM DELETE' : 'DELETE'}
              </button>
            ) : null}
          </div>
          {exMsg ? (
            <div className="mt-2 font-mono text-[10px]" style={{ color: exMsg.warn ? WARN : OK }}>
              {exMsg.text}
            </div>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

// ── Replay controls, shared by the workbench and the phone scrub bar ─────────

function useReplayControls(
  session: FlightSession,
  status: ReplayStatus | null,
  onStatus: (status: ReplayStatus) => void,
) {
  const canCommand = useCanCommand();
  const busy = useRef(false);

  const isThis = status?.sessionId === session.sessionId;
  const playing = isThis && status?.state === 'playing';
  const duration = isThis && status ? status.duration : (session.flightTimeS ?? 0);
  const offset = isThis && status ? status.offset : 0;
  const speed = isThis && status ? status.speed : 1;
  const t = duration > 0 ? Math.min(1, offset / duration) : 0;

  const call = useCallback(
    async (action: () => Promise<{ ok: boolean; data: ReplayStatus | null }>) => {
      if (busy.current) return;
      busy.current = true;
      try {
        const result = await action();
        if (result.ok && result.data) onStatus(result.data);
      } finally {
        busy.current = false;
      }
    },
    [onStatus],
  );

  const togglePlay = () => {
    if (!canCommand) return;
    if (!isThis) void call(() => loadReplay(session.sessionId).then((r) => (r.ok ? playReplay() : r)));
    else void call(() => (playing ? pauseReplay() : playReplay()));
  };

  return { canCommand, isThis, playing, duration, offset, speed, t, call, togglePlay };
}

/**
 * Replay on a phone. The lane workbench needs the full width and 222px of
 * height, so a phone hid it, and with it the only play button: a sortie could
 * be read but never replayed. This is the transport and a scrub slider, inside
 * the summary sheet. styles.css shows it only in the phone layout.
 */
function CompactReplay({
  session,
  status,
  onStatus,
}: {
  session: FlightSession;
  status: ReplayStatus | null;
  onStatus: (status: ReplayStatus) => void;
}) {
  const { canCommand, isThis, playing, duration, offset, t, call, togglePlay } =
    useReplayControls(session, status, onStatus);

  return (
    <div className="flt-scrub hidden flex-col gap-2 rounded-[14px] border border-white/8 bg-white/5 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[8.5px]">REPLAY</span>
        <span className="ml-auto whitespace-nowrap font-mono text-[10px] text-white/55">
          {mmss(offset)} / {mmss(duration)}
        </span>
      </div>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={togglePlay}
          disabled={!canCommand}
          aria-label={playing ? 'Pause replay' : 'Play replay'}
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/90 text-[12px] text-[#0c0c0d] shadow disabled:opacity-40"
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <button
          type="button"
          onClick={() => canCommand && void call(() => stopReplay())}
          disabled={!canCommand || !isThis}
          aria-label="Stop replay"
          className="flex size-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/7 text-[10px] text-white disabled:opacity-40"
        >
          ■
        </button>
        <input
          type="range"
          min={0}
          max={1000}
          value={Math.round(t * 1000)}
          disabled={!canCommand || !isThis || duration <= 0}
          aria-label="Replay position"
          onChange={(event) => {
            const fraction = Number(event.target.value) / 1000;
            void call(() => seekReplay(fraction * duration));
          }}
          className="h-11 min-w-0 flex-1 accent-white disabled:opacity-40"
        />
      </div>
      {!canCommand ? (
        <p className="text-[10px] text-white/45">Replay needs operator access.</p>
      ) : null}
    </div>
  );
}

// ── Bottom: telemetry workbench ──────────────────────────────────────────────

function Workbench({
  session,
  points,
  events,
  status,
  onStatus,
}: {
  session: FlightSession;
  points: TelemetryPoint[];
  events: FlightEvent[];
  status: ReplayStatus | null;
  onStatus: (status: ReplayStatus) => void;
}) {
  const { canCommand, isThis, playing, duration, offset, speed, t, call, togglePlay } =
    useReplayControls(session, status, onStatus);

  const lanes = useMemo(() => buildLanes(points, t), [points, t]);
  const start = session.startTime;

  return (
    <section className="flt-workbench glass pointer-events-auto absolute left-[14px] right-[14px] bottom-[14px] flex h-[222px] flex-col overflow-hidden rounded-[18px]">
      <div className="flex flex-none flex-wrap items-center gap-2.5 px-4 pt-2.5 pb-1.5">
        <span className="label-mono text-[8.5px]">
          TELEMETRY WORKBENCH — {session.droneId} · {stamp(session.startTime)} · {mmss(duration)}
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={togglePlay}
            disabled={!canCommand}
            title={canCommand ? 'Play / pause replay' : 'Replay needs operator access'}
            className="flex size-[30px] items-center justify-center rounded-full bg-white/90 text-[11px] text-[#0c0c0d] shadow disabled:opacity-40"
          >
            {playing ? '❚❚' : '▶'}
          </button>
          <button
            type="button"
            onClick={() => canCommand && void call(() => stopReplay())}
            disabled={!canCommand || !isThis}
            className="flex size-[30px] items-center justify-center rounded-full border border-white/15 bg-white/7 text-[9px] text-white disabled:opacity-40"
          >
            ■
          </button>
          {WORKBENCH_SPEEDS.map((x) => (
            <button
              type="button"
              key={x}
              onClick={() => canCommand && isThis && void call(() => setReplaySpeed(x))}
              disabled={!canCommand || !isThis}
              className="rounded-full px-2.5 py-[5px] font-mono text-[9px] font-bold disabled:opacity-40"
              style={{
                background: speed === x ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.06)',
                color: speed === x ? '#0c0c0d' : MUTED,
              }}
            >
              {x}×
            </button>
          ))}
          <span className="whitespace-nowrap font-mono text-[10px] text-white/55">
            {mmss(offset)} / {mmss(duration)}
          </span>
        </span>
      </div>

      <div
        className="relative min-h-0 flex-1 px-4 pb-2"
        style={{ cursor: canCommand && isThis ? 'crosshair' : undefined }}
        title={canCommand && isThis ? 'Click to seek the replay' : undefined}
        onClick={(event) => {
          // Click-to-seek: the whole lane strip is one shared time axis, so a
          // click's horizontal fraction IS the replay offset.
          if (!canCommand || !isThis || duration <= 0) return;
          const rect = event.currentTarget.getBoundingClientRect();
          const fraction = (event.clientX - rect.left - 16) / (rect.width - 32);
          const clamped = Math.max(0, Math.min(1, fraction));
          void call(() => seekReplay(clamped * duration));
        }}
      >
        <div
          className="pointer-events-none absolute top-0 bottom-[26px] z-[2] w-[1.6px] bg-white/90"
          style={{ left: `calc(16px + ${t} * (100% - 32px))` }}
        />
        <div className="flex h-full flex-col gap-[3px]">
          {lanes.map((lane) => (
            <div key={lane.key} className="flex min-h-0 flex-1 items-center gap-2.5">
              <span className="w-[34px] flex-none text-right font-mono text-[8px] font-semibold text-white/45">
                {lane.key}
              </span>
              <svg
                viewBox="0 0 600 30"
                preserveAspectRatio="none"
                className="block h-full flex-1 rounded-[6px] bg-black/20"
              >
                {lane.empty ? null : (
                  <polyline points={lane.points} fill="none" stroke={lane.color} strokeWidth="1.4" />
                )}
              </svg>
              <span
                className="w-[56px] flex-none whitespace-nowrap font-mono text-[9.5px]"
                style={{ color: lane.valueColor }}
              >
                {lane.value}
              </span>
            </div>
          ))}
          <div className="flex h-[20px] flex-none items-center gap-2.5">
            <span className="w-[34px] flex-none text-right font-mono text-[8px] font-semibold text-white/45">
              EVT
            </span>
            <div className="relative h-[14px] flex-1 rounded-[6px] bg-black/20">
              {duration > 0 &&
                events.map((event, index) => {
                  const x = Math.max(0, Math.min(1, (event.timestamp - start) / duration));
                  return (
                    <span
                      key={index}
                      title={`${event.eventType}${event.message ? ` — ${event.message}` : ''}`}
                      className="absolute top-[2px] bottom-[2px] w-[2.4px] rounded-[2px]"
                      style={{ left: `${(x * 100).toFixed(2)}%`, background: ledgerColor(event.severity) }}
                    />
                  );
                })}
            </div>
            <span className="w-[56px] flex-none" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Composition ──────────────────────────────────────────────────────────────

function FlightsWorkspace({
  sessions,
  loading,
  statistics,
  storage,
  selected,
  selectedId,
  points,
  events,
  status,
  onSelect,
  onStatus,
  onRefresh,
  onDeleted,
  onNotesSaved,
  filters,
  onToggle,
}: {
  sessions: FlightSession[];
  loading: boolean;
  statistics: FlightStatistics | null;
  storage: StorageReport | null;
  onNotesSaved: (notes: FlightNotes) => void;
  selected: FlightSession | null;
  selectedId: string | null;
  points: TelemetryPoint[];
  events: FlightEvent[];
  status: ReplayStatus | null;
  onSelect: (id: string) => void;
  onStatus: (status: ReplayStatus) => void;
  onRefresh: () => void;
  onDeleted: () => void;
  filters: { real: boolean; bench: boolean };
  onToggle: (which: 'real' | 'bench') => void;
}) {
  // Replay drives the live telemetry path on purpose, so a replayed aircraft
  // is published to the fleet exactly like a flying one — but this screen never
  // mounted the marker layer, so it had nowhere to appear. The track drew and
  // the aircraft did not, which reads as a replay that is not running.
  useDroneMarkers({ colorFor: teamColorFor });
  // Position at the scrub point, so dragging the playhead moves something on
  // the map even while paused, when no telemetry frame is being emitted.
  useFlightTrack(points, cursorFor(status, points.length));
  const replayLoaded = Boolean(status && status.sessionId);
  const top = replayLoaded ? '132px' : '86px';
  const tier = useViewport();
  // Tablet: the list is a drawer, so the summary and the map share the width.
  const [listOpen, setListOpen] = useState(false);
  // Landscape phone: one sheet, switched between the list and the summary.
  const [phoneTab, setPhoneTab] = useState<'list' | 'detail'>('list');
  const showList = tier !== 'tablet' || listOpen;

  return (
    <div
      className="pointer-events-none absolute inset-0 z-10"
      style={{ '--flt-top': top } as CSSProperties}
      data-flt-tab={phoneTab}
    >
      {tier === 'tablet' ? (
        <div className="pointer-events-auto absolute left-[14px] top-[var(--flt-top)] z-20">
          <button
            type="button"
            aria-expanded={listOpen}
            onClick={() => setListOpen((v) => !v)}
            className="glass-pill hit-44 rounded-[99px] px-[14px] py-2 label-mono text-[9.5px]"
            style={{ letterSpacing: '0.08em', background: listOpen ? 'oklch(1 0 0 / 0.16)' : undefined }}
          >
            {listOpen ? `◂ FLIGHTS · ${sessions.length}` : `FLIGHTS · ${sessions.length} ▸`}
          </button>
        </div>
      ) : null}

      <div
        className="flt-tabs glass-pill pointer-events-auto absolute hidden gap-1 rounded-[99px] p-1"
        role="tablist"
        style={{
          top: 'calc(var(--safe-t) + 66px)',
          right: 'calc(var(--safe-r) + 8px)',
          width: 'min(400px, 56vw)',
        }}
      >
        {(['list', 'detail'] as const).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={phoneTab === id}
            onClick={() => setPhoneTab(id)}
            className="min-h-[36px] flex-1 rounded-[99px] label-mono text-[9.5px]"
            style={{
              background: phoneTab === id ? 'oklch(1 0 0 / 0.92)' : 'transparent',
              color: phoneTab === id ? '#0c0c0d' : 'var(--muted-foreground-3)',
            }}
          >
            {id === 'list' ? `FLIGHTS · ${sessions.length}` : 'SELECTED'}
          </button>
        ))}
      </div>

      {replayLoaded && status ? (
        <div
          className="flt-replay-banner pointer-events-auto absolute top-[84px] left-1/2 flex max-w-[calc(100vw-28px)] -translate-x-1/2 items-center gap-2.5 rounded-full px-5 py-2 text-[12px]"
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.09), rgba(255,255,255,0.02)), oklch(0.13 0 0 / 0.7)',
            border: `1.5px solid color-mix(in oklch, ${WARN} 70%, transparent)`,
            backdropFilter: 'blur(24px) saturate(160%)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.55)',
          }}
        >
          <TriangleAlert className="size-3.5 flex-none" style={{ color: WARN }} />
          <span className="font-mono text-[10px] font-bold tracking-wide" style={{ color: WARN }}>
            REPLAY LOADED
          </span>
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {status.droneId} is replaying and <b>appears in the live fleet</b>
          </span>
          <button
            type="button"
            onClick={() => void unloadReplay().then((r) => r.ok && r.data && onStatus(r.data))}
            className="rounded-full border-none bg-white/90 px-2.5 py-[3px] font-mono text-[9px] font-bold text-[#0c0c0d]"
          >
            UNLOAD
          </button>
        </div>
      ) : null}

      {showList ? (
        <FlightList
          sessions={sessions}
          loading={loading}
          statistics={statistics}
          storage={storage}
          selectedId={selectedId}
          replaySessionId={status?.sessionId ?? null}
          onSelect={(id) => {
            onSelect(id);
            // Picking a sortie in the landscape sheet is asking to read it.
            if (tier === 'phone') setPhoneTab('detail');
          }}
          onRefresh={onRefresh}
          filters={filters}
          onToggle={onToggle}
        />
      ) : null}

      {selected ? (
        <>
          <FlightSummary
            session={selected}
            points={points}
            events={events}
            onDeleted={onDeleted}
            onNotesSaved={onNotesSaved}
            status={status}
            onStatus={onStatus}
          />
          <Workbench
            session={selected}
            points={points}
            events={events}
            status={status}
            onStatus={onStatus}
          />
        </>
      ) : (
        <div className="flt-detail glass pointer-events-auto absolute right-[14px] top-[var(--flt-top)] bottom-[250px] flex w-[var(--flt-detail-w)] items-center justify-center rounded-[20px] px-6 text-center text-sm text-muted-foreground">
          Select a sortie to see its track, its command ledger and its replay controls.
        </div>
      )}
    </div>
  );
}

export function FlightsView(): ReactNode {
  const aorId = useWorkspaceStore((state) => state.aor);
  const aor = aorById(aorId);

  const [sessions, setSessions] = useState<FlightSession[]>([]);
  const [statistics, setStatistics] = useState<FlightStatistics | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [status, setStatus] = useState<ReplayStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState<TelemetryPoint[]>([]);
  const [events, setEvents] = useState<FlightEvent[]>([]);
  const [filters, setFilters] = useState({ real: true, bench: false });
  const [storage, setStorage] = useState<StorageReport | null>(null);
  const seesStorage = useHasRole('tactical');

  // Sessions launched inside the active AOR only — no cross-AOR bleed. A sortie
  // with no launch fix can't be attributed elsewhere, so it stays visible.
  // REAL/BENCH then partitions by whether the aircraft actually flew.
  const scoped = useMemo(
    () =>
      sessions.filter((s) => {
        if (s.startLat !== null && s.startLon !== null && !positionInAor(s.startLat, s.startLon, aor))
          return false;
        return isBench(s) ? filters.bench : filters.real;
      }),
    [sessions, aor, filters],
  );

  const refresh = useCallback(async () => {
    const [list, stats] = await Promise.all([listFlights({ limit: 200 }), getFlightStatistics()]);
    if (list.ok) setSessions(list.data.sessions);
    if (stats.ok) setStatistics(stats.data);
    setLoading(false);
  }, []);

  // Asked only by a role the server answers, so an operator's screen never
  // fires a request that is certain to be refused.
  useEffect(() => {
    if (!seesStorage) {
      setStorage(null);
      return;
    }
    let cancelled = false;
    void getStorage().then((result) => {
      if (!cancelled && result.ok) setStorage(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [seesStorage]);

  useEffect(() => {
    void refresh();
    void getReplayStatus().then((result) => {
      if (result.ok) setStatus(result.data);
    });
  }, [refresh]);

  // Keep a valid selection within the current AOR's scoped list.
  useEffect(() => {
    setSelectedId((current) =>
      current && scoped.some((s) => s.sessionId === current)
        ? current
        : (scoped[0]?.sessionId ?? null),
    );
  }, [scoped]);

  // Selected sortie's telemetry + events.
  useEffect(() => {
    if (!selectedId) {
      setPoints([]);
      setEvents([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const [telemetry, eventList] = await Promise.all([
        getFlightTelemetry(selectedId),
        getFlightEvents(selectedId),
      ]);
      if (cancelled) return;
      setPoints(telemetry.ok ? telemetry.data : []);
      setEvents(eventList.ok ? eventList.data : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Only poll the replay engine while it is actually playing.
  useEffect(() => {
    if (status?.state !== 'playing') return undefined;
    const timer = setInterval(() => {
      void getReplayStatus().then((result) => {
        if (result.ok) setStatus(result.data);
      });
    }, REPLAY_POLL_MS);
    return () => clearInterval(timer);
  }, [status?.state]);

  const selected = scoped.find((s) => s.sessionId === selectedId) ?? null;

  return (
    <MapCanvas
      center={{ lat: aor.center[1], lon: aor.center[0] }}
      zoom={11}
      pitch={0}
    >
      <FlightsWorkspace
        sessions={scoped}
        loading={loading}
        statistics={statistics}
        storage={storage}
        selected={selected}
        selectedId={selectedId}
        points={points}
        events={events}
        status={status}
        onSelect={setSelectedId}
        onStatus={setStatus}
        onRefresh={() => void refresh()}
        onDeleted={() => void refresh()}
        onNotesSaved={(saved) =>
          setSessions((list) =>
            list.map((s) =>
              s.sessionId === saved.sessionId
                ? { ...s, notes: saved.notes, notesBy: saved.notesBy, notesAt: saved.notesAt }
                : s,
            ),
          )
        }
        filters={filters}
        onToggle={(which) => setFilters((f) => ({ ...f, [which]: !f[which] }))}
      />
    </MapCanvas>
  );
}

export default FlightsView;
